import { createContext, ReactNode, useContext, useRef, useState } from "react";
import { notaFiscalProduto } from "../interfaces/notaFiscal/notaFiscalProduto";
import { FileContext } from "./FileContextProvider";
import {
  scannerCodeWithOptionalQuantityPrefixFormat,
  scannerCodeWithOptionalQuantityPrefixValidadeFormat,
} from "../utils/regexPatternsUtils";
import { composeUniqueKey, isEanBarcodeLength, isPresentOnNotaFiscal } from "../utils/notaFiscalUtils";
import { createScannerItem } from "../utils/scannerUtils";
import { createScannerItemType } from "../interfaces/scanner/createScannerItem";
import { playErrorAudio } from "../utils/audioUtils";
import { isEmpty } from "../utils/dataCheckUtils";
import { lastUpdatedProduct } from "../interfaces/lastUpdatedProduct";

interface ScannerContextType {
  isScanOn: boolean;
  isScannedCodeValid: boolean;
  scannerArray: notaFiscalProduto[];
  scannedCode: string;
  keyDownEventCount: number;
  updateScannerArray: (newArray: notaFiscalProduto[]) => void;
  lastUpdatedProduct: lastUpdatedProduct | null;
  startScan: () => void;
  handleKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  handleInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  registerScannerRef: (ref: HTMLInputElement | null) => void;
  handleFocusOnScanner: () => void;
  updateProductName: (uniqueKey: string, newName: string) => void;
}

export const ScannerContext = createContext<ScannerContextType>({
  isScanOn: false,
  isScannedCodeValid: true,
  keyDownEventCount: 0,
  scannerArray: [],
  updateScannerArray: () => {},
  lastUpdatedProduct: null,
  scannedCode: "",
  startScan: () => {},
  handleKeyDown: () => {},
  handleInputChange: () => {},
  registerScannerRef: () => {},
  handleFocusOnScanner: () => {},
  updateProductName: () => {},
});

export default function ScannerContextProvider({ children }: { children: ReactNode }) {
  const defaultPrefixCalculation: string = "1.0000";

  const { notaFiscalProductsArray } = useContext(FileContext);

  const [isScanOn, setIsScanOn] = useState(false);
  const [scannedCode, setScannedCode] = useState("");
  const [scannerArray, setScannerArray] = useState<notaFiscalProduto[]>([]);
  const [lastUpdatedProduct, setLastUpdatedProduct] = useState<lastUpdatedProduct | null>(null);
  const [isScannedCodeValid, setIsScannedCodeValid] = useState(true);
  const [keyDownEventCount, setKeyDownEventCount] = useState<number>(0);

  const scannerRef = useRef<HTMLInputElement | null>(null);

  function updateItemScannerArray(barcode: string, total: string) {
    const scannerItem: createScannerItemType = {
      barcode: barcode,
      total: total,
      notaFiscalProductsArray: notaFiscalProductsArray,
    };
    const createItem = createScannerItem(scannerItem);

    const isItemValid =
      isEmpty<notaFiscalProduto>(notaFiscalProductsArray) ||
      isPresentOnNotaFiscal(scannerItem.barcode, scannerItem.notaFiscalProductsArray);

    if (!isItemValid) {
      playErrorAudio();
    }
    setScannerArray((prevArray) => [...prevArray, createItem]);
    setLastUpdatedProduct((prev) => ({ rowKey: composeUniqueKey(createItem), key: prev === null ? 1 : prev.key + 1 }));
  }

  //Scanner input Actions

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    event.preventDefault();
    const barcodeValue = event.currentTarget.value.trim();
    if (scannerCodeWithOptionalQuantityPrefixFormat.test(barcodeValue)) {
      setScannedCode(barcodeValue);
    }
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      event.preventDefault();

      const currentBarcodeValue = event.currentTarget.value.trim();
      const matchResult = currentBarcodeValue.match(scannerCodeWithOptionalQuantityPrefixValidadeFormat);

      if (!currentBarcodeValue) {
        return;
      }

      if (!matchResult) {
        setIsScannedCodeValid(false);
        setKeyDownEventCount((prev) => prev + 1);
        return;
      }

      const prefixCalculation = matchResult[1];
      const barcodeOnly = matchResult[2].trim();

      if (!isValidScannerProductIdentifier(barcodeOnly)) {
        setIsScannedCodeValid(false);
        setKeyDownEventCount((prev) => prev + 1);
        return;
      }

      if (!prefixCalculation) {
        updateItemScannerArray(barcodeOnly, defaultPrefixCalculation);
        setScannedCode("");
        setIsScannedCodeValid(true);
        setKeyDownEventCount(0);
        return;
      }

      const prefixCalculationNumber = parseFloat(prefixCalculation.replace(",", "."));

      if (prefixCalculationNumber !== 0) {
        updateItemScannerArray(barcodeOnly, prefixCalculation);
        setScannedCode("");
        setIsScannedCodeValid(true);
        setKeyDownEventCount(0);
        return;
      }
    }
  }
  function isValidScannerProductIdentifier(scannerProductIdentifier: string): boolean {
    const trimmedScannerProductIdentifier = scannerProductIdentifier.trim();

    if (!trimmedScannerProductIdentifier) {
      return false;
    }

    const isValidEanIdentifier = isEanBarcodeLength(trimmedScannerProductIdentifier);

    if (isValidEanIdentifier) {
      return true;
    }

    const isValidCProdIdentifierLength = trimmedScannerProductIdentifier.length >= 1 && trimmedScannerProductIdentifier.length <= 60;

    return isValidCProdIdentifierLength;
  }

  function updateScannerArray(updatedArray: notaFiscalProduto[]) {
    setScannerArray(updatedArray);
  }

  function registerScannerRef(ref: HTMLInputElement | null) {
    scannerRef.current = ref;
  }

  function handleFocusOnScanner() {
    scannerRef.current?.focus();
  }
  function updateProductName(uniqueKey: string, newName: string) {
    setScannerArray((prev) => prev.map((product) => (composeUniqueKey(product) === uniqueKey ? { ...product, xProd: newName } : product)));
  }

  function startScan() {
    setIsScanOn(true);
  }

  const values: ScannerContextType = {
    isScanOn: isScanOn,
    isScannedCodeValid: isScannedCodeValid,
    scannerArray: scannerArray,
    updateScannerArray: updateScannerArray,
    lastUpdatedProduct: lastUpdatedProduct,
    scannedCode: scannedCode,
    startScan,
    handleKeyDown: handleKeyDown,
    handleInputChange: handleInputChange,
    keyDownEventCount: keyDownEventCount,
    handleFocusOnScanner: handleFocusOnScanner,
    registerScannerRef: registerScannerRef,
    updateProductName,
  };

  return <ScannerContext.Provider value={values}>{children}</ScannerContext.Provider>;
}
